import React from "react";
import { NavLink } from "react-router-dom";
import { closeModalAction } from "../../../../components/Modal/reducer";
import { FORM_ITEM_TEXT } from "../../../../components/Form/FormItems/FormItemText";
import { FORM_ITEM_CHECKBOX } from "../../../../components/Form/FormItems/FormItemCheckbox";
import { FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING } from "../../../../components/Form/FormItems/FormItemResourceSelectLazyLoading";
import Tooltip from "../../../../components/Tooltip";
import { PAGE_DOM_SETTINGS } from "../../routes";

export const MODAL_TEMPLATE_FORM = "MODAL_TEMPLATE_FORM";

/**
 * Builds the field configuration array for the Template form.
 *
 * Returns the descriptor array consumed by `renderDynamicallyFields`.
 * Includes: title, version, cover, header (with settings link), footer
 * (with settings link), and active checkbox (new records only).
 *
 * @param {object}   params
 * @param {object}   params.data                    - Current modal record (may be undefined for new).
 * @param {boolean}  params.isEdit                  - True when editing an existing template.
 * @param {object}   params.form                    - Ant Design form instance.
 * @param {Function} params.t                       - i18next translation function.
 * @param {Function} params.dispatch                - Redux dispatch function.
 * @param {boolean}  params.canManageHeadersFooters - Permission to manage headers/footers.
 * @returns {Array<object>} Field descriptor array for renderDynamicallyFields.
 */
export const buildTemplateFormFields = ({ data, isEdit, form, t, dispatch, canManageHeadersFooters }) => [
  {
    required: true,
    type: FORM_ITEM_TEXT,
    name: "template_title",
    label: "qam.new_section.title",
    placeholder: "qam.new_section.title"
  },
  {
    required: true,
    type: FORM_ITEM_TEXT,
    name: "template_version",
    label: "container.general_words.version",
    placeholder: "container.general_words.version"
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "cover_id",
    label: "dom.settings.cover",
    placeholder: "dom.settings.cover",
    initialValue: data?.validVersionHistory?.cover
      ? { id: data.validVersionHistory.cover.id, text: data.validVersionHistory.cover.name }
      : undefined,
    apiRoute: "dom/covers",
    fieldProps: {
      customParams: {
        order_by_column: ["name"],
        order_by_type: ["asc"],
        field: ["active"],
        op: ["eq"],
        term: [1]
      }
    }
  },
  {
    className: "hide-required-mark",
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "header_id",
    placeholder: "qam.catalogues.header",
    initialValue: data?.validVersionHistory?.header
      ? { id: data.validVersionHistory.header.id, text: data.validVersionHistory.header.name }
      : undefined,
    apiRoute: "dom/headers",
    label: (
      <div>
        {t("qam.catalogues.header")} *
        {canManageHeadersFooters && (
          <Tooltip title={t("dom.settings.new_header")} className="ml-10 text-primary">
            <NavLink
              to={{ pathname: PAGE_DOM_SETTINGS, tab: t("dom.settings.headers") }}
              onClick={() => dispatch(closeModalAction({ name: MODAL_TEMPLATE_FORM }))}
            >
              <i className="ml-10 ti-plus" />
            </NavLink>
          </Tooltip>
        )}
      </div>
    ),
    fieldProps: {
      overwritePreviousOptions: true,
      searchColumns: ["name"],
      requestOnMount: true,
      customParams: {
        order_by_column: ["name"],
        order_by_type: ["asc"],
        field: ["active"],
        op: ["eq"],
        term: [1]
      },
      onGetOptions: (options) => {
        if (!form.getFieldValue("header_id")) {
          const header = options.find((header) => header.defaultCurrentId) || {};
          form.setFieldsValue({ header_id: header?.defaultCurrentId });
        }
        return options;
      }
    }
  },
  {
    className: "hide-required-mark",
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "footer_id",
    placeholder: "dom.settings.footer",
    initialValue: data?.validVersionHistory?.footer
      ? { id: data.validVersionHistory.footer.id, text: data.validVersionHistory.footer.name }
      : undefined,
    apiRoute: "dom/footers",
    label: (
      <div>
        {t("dom.settings.footer")} *
        {canManageHeadersFooters && (
          <Tooltip title={t("dom.settings.new_footer")} className="ml-10 text-primary">
            <NavLink
              to={{ pathname: PAGE_DOM_SETTINGS, tab: t("dom.settings.footers") }}
              onClick={() => dispatch(closeModalAction({ name: MODAL_TEMPLATE_FORM }))}
            >
              <i className="ml-10 ti-plus" />
            </NavLink>
          </Tooltip>
        )}
      </div>
    ),
    fieldProps: {
      overwritePreviousOptions: true,
      searchColumns: ["name"],
      requestOnMount: true,
      customParams: {
        order_by_column: ["name"],
        order_by_type: ["asc"],
        field: ["active"],
        op: ["eq"],
        term: [1]
      },
      onGetOptions: (options) => {
        if (!form.getFieldValue("footer_id")) {
          const footer = options.find((footer) => footer.defaultCurrentId) || {};
          form.setFieldsValue({ footer_id: footer?.defaultCurrentId });
        }
        return options;
      }
    }
  },
  {
    type: FORM_ITEM_CHECKBOX,
    visible: !isEdit,
    name: "active",
    label: "container.general_words.active"
  }
];
